/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:12:48 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import {
  schemaBuilder,
  Many2OneIntPKBaseSchema,
  Many2ManyIntPKBaseSchema,
  Many2OneUUIDPKBaseSchema,
  Many2ManyUUIDPKBaseSchema,
} from "lib/validation";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
  AIAgentConfiguration,
  ContentTag,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";
import { UUID4, EnumSubset } from "lib/types";

export enum NODE_TYPES {
  // Start = "Start Event",
  StartFunction = "Start Event",
  View = "User Task",
  Call = "Call Activity",
  Event = "Intermediate Event",
  Job = "Job (Scheduled Task)",
  If = "If Gateway",
  Switch = "Switch Gateway",
  Split = "Split",
  Join = "Merge",
  Function = "Function",
  Handler = "Service (Automated Task)",
  Signal = "Signal",
  End = "End Event",
}

export type AUTO_NODE_TYPES = EnumSubset<typeof NODE_TYPES, "StartFunction" | "Handler" | "Job" | "End">;

export enum USER_TASK_TYPES {
  billing = "billing",
  generate_document = "generate_document",
  review = "review",
  sign = "sign",
  resubmission = "resubmission",
  custom = "custom",
  survey = "survey",
  archive = "archive",
  assignment = "assignment",
  move = "move",
  payment = "payment",
  payment_confirmation = "payment_confirmation",
}

export enum TASK_ASSIGNEE_TYPES {
  individual = "Individual (Assign to a single user)",
  team = "Teams (Assign to one or more teams)",
}

export type IUserTaskTypesData = {
  [key: string]: {
    name: string;
    description: string;
  };
};

export enum AUTOMATED_TASK_TYPES {
  ai_task = "ai_task",
  send_notification = "send_notification",
  auto_billing = "auto_billing",
  generate_document = "generate_document",
  update_dataset = "update_dataset",
  web_service_call = "web_service_call",
}

export enum TASK_DEFINITION_UPDATE_ACTIONS {
  delete_outgoing_branch = "delete_outgoing_branch",
}

interface FlowChartPosition {
  x_position_factor: number;
  y_position_factor: number;
}

export interface TaskTemplateExtraData {
  flowchart_position: FlowChartPosition;
  only_assigner_can_reassign?: boolean;
}

export interface IOutgoingBranchSummary {
  activation_settings_id: number;
  next_id: TaskTemplate["id"];
}

type TSettingsForUndoneStatePreservation = {
  preserve_owner?: boolean;
  preserve_review_form_entry?: boolean;
  reactivate_cancelled_tasks_for_stage?: boolean;
};

export interface DownstreamProcessConfiguration extends ProcessTemplateArtifact, WritableModel<false> {
  configuration_title: string;
  downstream_process_definition: number;
  initiating_task_templates?: TaskTemplate["id"][];
  allow_multi_instance: boolean;
  maximum_instances?: number;
  auto_create_on_node_activation: boolean;
  downstream_initiation_requires_upstream_instance: boolean;
  show_downstream_tasks?: boolean;
  show_downstream_history?: boolean;
  readonly downstream_process_definition_detail?: {
    title: string;
    service_code: string;
  };
}

export enum EventDirection {
  catch = "Catch (Listens to event)",
  throw = "Throw (Dispatches event)",
}

export enum EventType {
  Timer = "Timer",
  Error = "Error",
  Signal = "Signal",
  Message = "Message",
  Escalation = "Escalation",
  Compensation = "Compensation",
}

export const CatchEventType = EventType;

export enum ThrowEventType {
  Signal = "Signal",
  Message = "Message",
  Escalation = "Escalation",
  Compensation = "Compensation",
}

export interface EventCode extends ProcessTemplateArtifact, WritableModel<false> {
  code: string;
  name: string;
  event_type: keyof typeof EventType;
  description?: string;
}

export enum TimerExpressionType {
  iso_8601 = "ISO 8601",
  cron = "Cron",
}

export interface EventConfigurationData {
  timer_expression?: string;
  timer_expression_type?: keyof typeof TimerExpressionType;
  timer_countdown_from?: "task_created" | "task_assigned" | "task_started";
}

export interface EventConfiguration extends ProcessTemplateArtifact, WritableModel<false> {
  title: string;
  event_code: EventCode["id"];
  event_direction: keyof typeof EventDirection;
  readonly event_code_label?: string;
  readonly event_type?: keyof typeof EventType;
  configuration?: EventConfigurationData;
}

export interface BoundaryEventConfiguration extends ProcessTemplateArtifact, WritableModel<false> {
  title: string;
  host_node: TaskTemplate["id"];
  target_node: TaskTemplate["id"];
  is_interrupting: boolean;
  event_configuration: EventConfiguration["id"];
  enabled: boolean;
  readonly host_node_title?: string;
  readonly target_node_title?: string;
  readonly event_code_label?: string;
  readonly event_type?: keyof typeof EventType;
}

export interface TaskTemplate extends ProcessTemplateArtifact, WritableModel<false> {
  process: number;
  title: string;
  readonly identifier?: string;
  sequence: number;
  next?: number;
  type: keyof typeof NODE_TYPES;
  enabled: boolean;
  user_action_type?: keyof typeof USER_TASK_TYPES;
  user_action_form?: number;
  client_task: boolean;
  assign_task: boolean;
  assign_title?: string;
  assign_form?: number;
  cancel_active: boolean;
  active_tasks_to_cancel?: TaskTemplate["id"][];
  automated_task_type?: keyof typeof AUTOMATED_TASK_TYPES;
  generate_document: boolean;
  document?: number;
  update_linked_entries: boolean;
  default_show_form: boolean;
  maximum_duration?: number;
  can_self_assign: boolean;
  stage?: number;
  default_reviewer_assign_task?: number;
  default_reviewer_permit_task?: number;
  ajax_source?: number;
  linked_task_to_activate?: number;
  digital_signature_method?: UUID4;
  document_tosign?: number;
  task_avail_date_form?: number;
  task_avail_date_field?: number;
  billing_configuration?: UUID4;
  data_output_association_node?: number;
  readonly permissions?: number[];
  readonly execute_permission?: number;
  readonly assign_permission?: number;
  readonly supervise_permission?: number;
  readonly undo_permission?: number;
  readonly outgoing_branches?: IOutgoingBranchSummary[];
  readonly process_template_title?: string;
  dataset_reference?: number;
  extra_data?: TaskTemplateExtraData;
  auto_redirect_on_start?: boolean;
  integration_connections?: string[];
  ai_task_instruction?: AIAgentConfiguration["id"];
  resubmit_allow_edit_all_fields?: boolean;
  can_be_undone?: boolean;
  undo_title?: string;
  undo_action_form?: number;
  allowed_undo_stages?: number[];
  tasks_to_cancel_on_undo?: TaskTemplate["id"][];
  task_owner_can_undo?: boolean;
  settings_for_undone_state_preservation?: TSettingsForUndoneStatePreservation;
  tags?: ContentTag["id"][];
  downstream_process_configurations?: DownstreamProcessConfiguration["id"][];
  boundary_events?: BoundaryEventConfiguration["id"][];
  readonly boundary_events_detailed?: BoundaryEventConfiguration[];
  triggering_boundary_events?: BoundaryEventConfiguration["id"][];
  event_configuration?: EventConfiguration["id"];
  readonly event_type?: keyof typeof EventType;
  readonly event_direction?: EventConfiguration["event_direction"];
}

const FlowChartPositionValidationSchema: schemaBuilder.SchemaOf<FlowChartPosition> = schemaBuilder.object().shape({
  x_position_factor: schemaBuilder.number().optional(),
  y_position_factor: schemaBuilder.number().optional(),
});

type WriteOnlyTaskTemplate = Omit<
  TaskTemplate,
  | "permissions"
  | "execute_permission"
  | "assign_permission"
  | "supervise_permission"
  | "undo_permission"
  | "outgoing_branches"
  | "process_template_title"
  | "boundary_events_detailed"
  | "event_type"
  | "event_direction"
>;

export const TaskTemplateValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyTaskTemplate> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.number().positive().nullable(),
    process: schemaBuilder.number().nullable().optional(),
    identifier: schemaBuilder.string().nullable(),
    title: schemaBuilder.string().required("You must provide the title for a configured task template."),
    sequence: schemaBuilder.number().required("Sequence is required."),
    next: schemaBuilder.number().nullable().optional(),
    type: schemaBuilder
      .mixed()
      .oneOf(Object.keys(NODE_TYPES))
      .required("Task Type is required")
      .default(NODE_TYPES.View),
    enabled: schemaBuilder.boolean().required(),
    user_action_type: schemaBuilder.string().nullable().optional() as schemaBuilder.SchemaOf<
      keyof typeof USER_TASK_TYPES
    >,
    user_action_form: schemaBuilder
      .number()
      .nullable()
      .optional()
      .when(["id", "user_action_type"], {
        is: (id: number, user_action_type: keyof typeof USER_TASK_TYPES) => id && user_action_type === "review",
        then: schemaBuilder.number().nullable().required("An assessment form is required for an assessment task."),
      }),
    client_task: schemaBuilder.boolean().required(),
    assign_task: schemaBuilder.boolean().required(),
    assign_title: schemaBuilder.string().nullable().optional(),
    assign_form: schemaBuilder.number().nullable().optional(),
    cancel_active: schemaBuilder.boolean().nullable().optional(),
    active_tasks_to_cancel: Many2ManyIntPKBaseSchema().nullable().optional(),
    automated_task_type: schemaBuilder.mixed().oneOf(Object.keys(AUTOMATED_TASK_TYPES)).optional(),
    generate_document: schemaBuilder.boolean().nullable().optional(),
    document: schemaBuilder.number().nullable().optional(),
    update_linked_entries: schemaBuilder.boolean().nullable().optional(),
    default_show_form: schemaBuilder.boolean().nullable().optional(),
    maximum_duration: schemaBuilder
      .number()
      .nullable()
      .optional()
      .default(0)
      .typeError("Maximum duration must be a number"),
    can_self_assign: schemaBuilder.boolean().nullable().optional(),
    stage: schemaBuilder.number().nullable().optional(),
    default_reviewer_assign_task: schemaBuilder.number().nullable().optional(),
    default_reviewer_permit_task: schemaBuilder.number().nullable().optional(),
    ajax_source: schemaBuilder.number().nullable().optional(),
    linked_task_to_activate: schemaBuilder.number().nullable().optional(),
    digital_signature_method: schemaBuilder.string().uuid().nullable().optional(),
    document_tosign: schemaBuilder.number().nullable().optional(),
    task_avail_date_form: schemaBuilder.number().nullable().optional(),
    task_avail_date_field: schemaBuilder.number().nullable().optional(),
    billing_configuration: schemaBuilder.string().uuid().nullable().optional(),
    data_output_association_node: schemaBuilder.number().nullable().optional(),
    dataset_reference: schemaBuilder.number().nullable().optional(),
    extra_data: FlowChartPositionValidationSchema.optional(),
    auto_redirect_on_start: schemaBuilder.boolean().nullable().optional(),
    integration_connections: Many2ManyUUIDPKBaseSchema().nullable().optional(),
    ai_task_instruction: Many2OneUUIDPKBaseSchema().nullable().optional(),
    resubmit_allow_edit_all_fields: schemaBuilder.boolean().nullable().optional(),
    can_be_undone: schemaBuilder.boolean().nullable().optional(),
    undo_title: schemaBuilder.string().nullable().optional(),
    undo_action_form: schemaBuilder.number().nullable().optional(),
    allowed_undo_stages: Many2ManyIntPKBaseSchema().optional().default([]),
    tasks_to_cancel_on_undo: Many2ManyIntPKBaseSchema().optional().default([]),
    task_owner_can_undo: schemaBuilder.boolean().nullable().optional(),
    settings_for_undone_state_preservation: schemaBuilder.mixed().nullable().optional(),
    tags: Many2ManyUUIDPKBaseSchema().nullable().optional(),
    downstream_process_configurations: Many2ManyIntPKBaseSchema().nullable().optional(),
    boundary_events: Many2ManyIntPKBaseSchema().nullable().optional(),
    triggering_boundary_events: Many2ManyIntPKBaseSchema().nullable().optional(),
    event_configuration: Many2OneIntPKBaseSchema()
      .nullable()
      .optional()
      .when(["type"], {
        is: (type: keyof typeof NODE_TYPES) => type === "Event",
        then: Many2OneIntPKBaseSchema().required("Event configuration is required for an intermediate event node."),
      }),
  });

export const DownstreamProcessConfigurationValidationSchema: ProcessTemplateArtifactSchemaType<
  Omit<DownstreamProcessConfiguration, "downstream_process_definition_detail">
> = ProcessTemplateArtifactValidationSchema.shape({
  id: schemaBuilder.number().positive().nullable(),
  configuration_title: schemaBuilder.string().required(),
  allow_multi_instance: schemaBuilder.boolean().required(),
  downstream_initiation_requires_upstream_instance: schemaBuilder.boolean().required(),
  maximum_instances: schemaBuilder
    .number()
    .nullable()
    .optional()
    .default(0)
    .typeError("Maximum instances must be a number")
    .transform((value, originalValue) => (originalValue === "" ? null : value)),
  downstream_process_definition: schemaBuilder.number().required(),
  auto_create_on_node_activation: schemaBuilder.boolean().required(),
  initiating_task_templates: Many2ManyIntPKBaseSchema().nullable().optional(),
  show_downstream_tasks: schemaBuilder.boolean().optional(),
  show_downstream_history: schemaBuilder.boolean().optional(),
});

export const EventCodeValidationSchema: ProcessTemplateArtifactSchemaType<EventCode> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.number().positive().nullable(),
    code: schemaBuilder.string().required(),
    name: schemaBuilder.string().required(),
    description: schemaBuilder.string().nullable().optional(),
    event_type: schemaBuilder.mixed().oneOf(Object.keys(EventType)).required("Event Type is required"),
  });

const EventConfigurationDataValidationSchema: schemaBuilder.SchemaOf<EventConfigurationData> = schemaBuilder
  .object()
  .shape({
    timer_expression: schemaBuilder.string().optional(),
    timer_expression_type: schemaBuilder.mixed().oneOf(Object.keys(TimerExpressionType)).optional(),
    timer_countdown_from: schemaBuilder.mixed().optional(),
  });

export const EventConfigurationValidationSchema: ProcessTemplateArtifactSchemaType<
  Omit<EventConfiguration, "event_code_label" | "event_type">
> = ProcessTemplateArtifactValidationSchema.shape({
  id: schemaBuilder.number().positive().nullable(),
  title: schemaBuilder.string().required("You must provide the title for an event configuration."),
  event_code: Many2OneIntPKBaseSchema().required(),
  event_direction: schemaBuilder
    .mixed()
    .required("Event direction is required for an event configuration.")
    .oneOf(Object.keys(EventDirection)),
  configuration: EventConfigurationDataValidationSchema.optional(),
});

export const BoundaryEventConfigurationValidationSchema: ProcessTemplateArtifactSchemaType<
  Omit<BoundaryEventConfiguration, "host_node_title" | "target_node_title" | "event_code_label" | "event_type">
> = ProcessTemplateArtifactValidationSchema.shape({
  id: schemaBuilder.number().positive().nullable(),
  title: schemaBuilder.string().required("You must provide the title for a configured boundary event."),
  host_node: Many2OneIntPKBaseSchema().required(),
  event_configuration: Many2OneIntPKBaseSchema().required(),
  target_node: Many2OneIntPKBaseSchema().required(),
  is_interrupting: schemaBuilder.boolean().required(),
  enabled: schemaBuilder.boolean().required(),
});
